import React from 'react';

/**
 * Shared furniture for the wrap-up editor.
 *
 * **NOTHING IS OWED.** Tom: *"the form has to make every section visibly
 * optional rather than presenting empty rows that look required."* Hence
 * `Block`: every section carries an Optional tag, and an unused one says
 * plainly that the client page skips it rather than showing blank fields.
 *
 * **HINTS ARE SENTENCES, SO THEY ARE SET AS SENTENCES.** `.label` and
 * `.label-sm` uppercase in CSS. They are for the system's own vocabulary —
 * tags, buttons, field captions — never for prose. Forty words of 10px
 * letterspaced capitals is unreadable, and this is the third place in the
 * app it has had to be undone.
 *
 * **WIDTH GOES ON THE WRAPPER, NOT THE INPUT.** `FIELD` in adminStyles
 * carries `w-full`, and Tailwind emits `w-full` after `w-24`, so adding a
 * width class alongside it does nothing — two fields on one row each
 * demanded the full width and wrapped onto separate lines.
 */

export const ROW = 'mt-6 border-t border-ruleDark pt-6 first:mt-0 first:border-0 first:pt-0';
export const AREA = 'mt-3 w-full resize-y border-0 border-b border-paper/25 bg-transparent pb-2 font-sans text-sm leading-relaxed tracking-wide2 text-paper placeholder:text-paper/30 focus:border-paper focus:outline-none';

/** Prose inside the editor: mixed case, never a label. */
export const HINT = 'mt-4 max-w-2xl text-sm leading-relaxed text-paper/40';
const QUIET = 'label text-paper/40 transition-colors duration-150 ease-out hover:text-paper';
const GHOST = 'label border border-paper/40 px-6 py-3.5 text-paper transition-colors duration-150 ease-out hover:border-paper';

/** The publish control. A real switch, so it is reachable by keyboard. */
export function Switch({
  on,
  onChange,
  label
}: {
  on: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={onChange} className={['flex h-8 w-14 shrink-0 rounded-full border p-[3px] transition-colors duration-150 ease-out', on ? 'justify-end border-paper bg-paper' : 'justify-start border-paper/30 bg-transparent'].join(' ')} data-id="element-1764" data-mp="lk7txmjx3ui2">
      <span className={['h-6 w-6 rounded-full transition-colors duration-150 ease-out', on ? 'bg-ink' : 'bg-paper/40'].join(' ')} data-id="element-1765" data-mp="2iiwytsdfdgej" />
    </button>;
}

/**
 * One optional section of the report. `filled` decides whether the body
 * shows or the not-included line does. Sections holding rows pass no
 * `onRemove` — they show a count and keep their Add button always.
 */
export function Block({
  title,
  hint,
  filled,
  count,
  addLabel,
  onAdd,
  onRemove,
  children
}: {
  title: string;
  hint?: string;
  filled: boolean;
  count?: string;
  addLabel: string;
  onAdd: () => void;
  onRemove?: () => void;
  children?: React.ReactNode;
}) {
  const isList = onRemove === undefined;
  return <section className="mt-5 border border-ruleDark px-7 py-7" data-id="element-1766" data-mp="1ltpe2mkmrbbo">
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-3" data-id="element-1767" data-mp="1avb17pxcmtb">
        <h3 className="label m-0 text-paper" data-id="element-1768" data-mp="685bdieuqivx">{title}</h3>
        <span className="label border border-ruleDark px-2 py-1 text-paper/40" data-id="element-1769" data-mp="66kfnpgswtwh">Optional</span>
        <span className="flex-grow" data-id="element-1770" data-mp="a8yy48gk5rn8" />
        {count && <span className="label m-0 text-paper/40" data-id="element-1771" data-mp="f1id99uw8uu5">{count}</span>}
        {filled && onRemove && <button type="button" onClick={onRemove} className={QUIET} data-id="element-1772" data-mp="k0l67vxlnalr">
            Remove
          </button>}
      </div>

      {hint && <p className={HINT} data-id="element-1773" data-mp="oxlca6ug8dcv">{hint}</p>}

      {filled ? <div className="mt-7" data-id="element-1774" data-mp="txpvw5qnlo7b">{children}</div> : <p className="mt-6 text-sm text-paper/40" data-id="element-1775" data-mp="tw4xg517xl46">Not included &mdash; the client page skips it.</p>}

      {(!filled || isList) && <button type="button" onClick={onAdd} className={`${GHOST} mt-7`} data-id="element-1776" data-mp="2vq476lyozuer">
          {addLabel}
        </button>}
    </section>;
}

/** A remove control on a single row inside a section. */
export function RowRemove({
  onClick,
  label
}: {
  onClick: () => void;
  label: string;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className={`${QUIET} shrink-0`} data-id="element-1777" data-mp="20fz44i5gha9h">
      Remove
    </button>;
}